import { useMutation, useQuery, useQueryClient } from 'react-query';
import { useAuth0 } from '@auth0/auth0-react';
import setToken from '../../utils/api';
import {
  AiSchedule,
  AiScheduleFromPromptResult,
  AiScheduleRun,
  AiScheduleUpdate,
} from '../../types/IAiSchedule';

export const AI_SCHEDULES_KEY = 'ai-schedules';
export const AI_SCHEDULE_KEY = 'ai-schedule';

const failure = (data: unknown, fallback: string) =>
  new Error(
    (data as { message?: string; Message?: string } | undefined)?.message ??
      (data as { Message?: string } | undefined)?.Message ??
      fallback,
  );

// The signed-in admin's schedules, most recently changed first.
export function useAiSchedules() {
  const { getAccessTokenSilently } = useAuth0();
  return useQuery<AiSchedule[], Error>(
    [AI_SCHEDULES_KEY],
    async () => {
      const token = await getAccessTokenSilently();
      const api = await setToken(token);
      const response = await api.get<AiSchedule[]>('api/ai/schedules');
      if (response.status !== 200)
        throw failure(response.data, 'Could not load schedules');
      return response.data;
    },
    { refetchOnWindowFocus: false },
  );
}

// One schedule with its recent runs; loaded when a card's run history is opened.
export function useAiSchedule(id: number | null) {
  const { getAccessTokenSilently } = useAuth0();
  return useQuery<AiSchedule, Error>(
    [AI_SCHEDULE_KEY, id],
    async () => {
      const token = await getAccessTokenSilently();
      const api = await setToken(token);
      const response = await api.get<AiSchedule>(`api/ai/schedules/${id}`);
      if (response.status !== 200)
        throw failure(response.data, 'Could not load the schedule');
      return response.data;
    },
    { enabled: id != null, refetchOnWindowFocus: false, staleTime: 30_000 },
  );
}

const useScheduleAction = <TVars, TResult = AiSchedule>(
  key: string,
  request: (
    api: Awaited<ReturnType<typeof setToken>>,
    vars: TVars,
  ) => Promise<{ status: number; data: unknown }>,
  fallback: string,
) => {
  const queryClient = useQueryClient();
  const { getAccessTokenSilently } = useAuth0();
  return useMutation<TResult, Error, TVars>(
    [key],
    async (vars) => {
      const token = await getAccessTokenSilently();
      const api = await setToken(token);
      const response = await request(api, vars);
      if (response.status < 200 || response.status >= 300)
        throw failure(response.data, fallback);
      return response.data as TResult;
    },
    {
      onSuccess: (_result, vars) => {
        const { id } = vars as { id?: number };
        queryClient.invalidateQueries([AI_SCHEDULES_KEY]);
        // Only this schedule's detail, and none at all for a deleted one: refetching a card
        // that is about to unmount would just produce a 404.
        if (id != null && key === 'delete-ai-schedule') {
          queryClient.removeQueries([AI_SCHEDULE_KEY, id]);
        } else if (id != null) {
          queryClient.invalidateQueries([AI_SCHEDULE_KEY, id]);
        }
      },
    },
  );
};

export const useUpdateAiSchedule = () =>
  useScheduleAction<{ id: number; update: AiScheduleUpdate }>(
    'update-ai-schedule',
    (api, { id, update }) => api.put(`api/ai/schedules/${id}`, update),
    'Could not save the schedule',
  );

export const usePauseAiSchedule = () =>
  useScheduleAction<{ id: number }>(
    'pause-ai-schedule',
    (api, { id }) => api.post(`api/ai/schedules/${id}/pause`),
    'Could not pause the schedule',
  );

export const useResumeAiSchedule = () =>
  useScheduleAction<{ id: number }>(
    'resume-ai-schedule',
    (api, { id }) => api.post(`api/ai/schedules/${id}/resume`),
    'Could not resume the schedule',
  );

export const useDeleteAiSchedule = () =>
  useScheduleAction<{ id: number }, void>(
    'delete-ai-schedule',
    (api, { id }) => api.delete(`api/ai/schedules/${id}`),
    'Could not delete the schedule',
  );

// Runs the schedule immediately and waits for the outcome (as long as one chat turn).
export const useRunAiScheduleNow = () =>
  useScheduleAction<{ id: number }, AiScheduleRun>(
    'run-ai-schedule',
    (api, { id }) => api.post(`api/ai/schedules/${id}/run`),
    'The run failed to start',
  );

// "Describe what you want and when": the text goes through the assistant like a chat message.
export const useCreateAiScheduleFromPrompt = () =>
  useScheduleAction<{ prompt: string }, AiScheduleFromPromptResult>(
    'create-ai-schedule',
    (api, { prompt }) => api.post('api/ai/schedules/from-prompt', { prompt }),
    'The assistant could not create the schedule',
  );
